import { useTheme } from '@mui/joy';
import { GridApi, ProcessCellForExportParams, StatusPanelDef } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { mapKeys, mapValues } from 'lodash';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useAuthUser } from 'auth/hooks';
import { useJobChecker } from 'cli/hooks';
import { ICliJob } from 'cli/types';
import { AgGridServerTable } from 'core/components';
import { TotalsStatusBar } from 'core/elements';
import { TableLayoutType } from 'core/enums';
import { AgGridServerTableProvider } from 'core/states';
import { AgGridGetRowsParams } from 'core/types';
import { IJob } from 'jobs/types';
import { orderPartsPath } from 'orders/routes/routePaths';
import { CustomerQuoteUpsert } from 'quotes/components/CustomerQuoteUpsert';
import { ProspectQuoteUpsert } from 'quotes/components/ProspectQuoteUpsert';
import { ArchiveQuoteButton, ReassignQuoteButton } from 'quotes/components/QuoteList';
import {
  QuoteEntityType,
  QuoteListAction,
  QuoteListType,
  QuoteState,
  QuoteStatus,
  QuoteStatusLabel,
  QuoteType,
} from 'quotes/enums';
import { quotePartsPath } from 'quotes/routes/routePaths';
import { quoteService } from 'quotes/services';
import { ICustomerQuote } from 'quotes/types';

import {
  CustomerQuoteColId,
  DuplicateQuoteButton,
  ExportQuotesButton,
  getColumnDef,
  getInitialState,
  UpdateQuotesButton,
} from './Table';
import { useActions } from './useActions';

interface IProps {
  entityType?: QuoteEntityType;
  job?: IJob;
  projectId?: number;
  quoteListType?: QuoteListType;
}

const CustomerQuoteList = ({
  entityType = QuoteEntityType.QUOTE,
  job,
  projectId,
  quoteListType = QuoteListType.GLOBAL,
}: IProps) => {
  const [pendingCoreCliJob, setPendingCoreCliJob] = useState<ICliJob>();

  const theme = useTheme();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { user } = useAuthUser();
  const { checkJobStatus } = useJobChecker();

  const { activeActionDrawer, canCreateProspectQuote, onCloseActionDrawer, actions } = useActions(
    entityType,
    quoteListType
  );

  const columnDefs = useMemo(
    () => getColumnDef(theme, entityType, { pendingCoreCliJob, quoteListType }),
    [entityType, pendingCoreCliJob, quoteListType, theme]
  );

  const initialState = useMemo(() => getInitialState(entityType, user), [entityType, user]);

  const tableId = useMemo(() => `customer${entityType}ListTable`, [entityType]);

  const tableEntityType = useMemo(() => `Customer${entityType}`, [entityType]);

  const layoutType = useMemo(() => {
    const isQuote = entityType === QuoteEntityType.QUOTE;

    if (quoteListType === QuoteListType.GLOBAL) {
      return isQuote ? TableLayoutType.CUSTOMER_QUOTE : TableLayoutType.CUSTOMER_ORDER;
    }

    return isQuote
      ? TableLayoutType.PROJECT_CUSTOMER_QUOTE
      : TableLayoutType.PROJECT_CUSTOMER_ORDER;
  }, [entityType, quoteListType]);

  const upsertCustomerQuoteDefaultValues = useMemo(() => {
    switch (quoteListType) {
      case QuoteListType.PROJECT:
        return { project: { id: projectId } };

      case QuoteListType.JOB:
        if (!job) {
          return {};
        }

        return {
          project: { id: job.projectId },
          jobName: job.name,
        };

      default:
        return {};
    }
  }, [quoteListType, projectId, job]);

  const startCheckForPendingCoreCliJob = useCallback(
    (gridApi: GridApi, cliJob?: ICliJob) => {
      if (!cliJob) {
        return;
      }

      setPendingCoreCliJob(cliJob);

      checkJobStatus(
        cliJob,
        (cliJob: ICliJob) => {
          const { orders } = cliJob.result as Record<string, ICustomerQuote[]>;

          setPendingCoreCliJob(undefined);

          gridApi.forEachNode((node) => {
            node.updateData({
              ...node.data,
              ...orders.find((order) => order.id === node.data?.id),
            });
          });
        },
        () => {
          setPendingCoreCliJob(undefined);
        }
      );
    },
    [checkJobStatus]
  );

  const mapFilters = useCallback((filters) => {
    let mappedFilters = filters;

    mappedFilters = mapKeys(mappedFilters, (_, key) => {
      switch (key) {
        case 'customerContactName':
          return 'customerContactUserId';
        default:
          return key;
      }
    });

    mappedFilters = mapValues(mappedFilters, (value, key) => {
      switch (key) {
        case 'archived':
          return 1 === value.length ? (value[0] === QuoteState.ACTIVE ? 0 : 1) : null;
        case 'status':
          return value.join('|');
        case 'closeConfidence':
          return `"${value}"`;
        default:
          return value;
      }
    });

    return mappedFilters;
  }, []);

  const getRows = useCallback(
    async ({ filters, ...rest }: AgGridGetRowsParams, gridRef?: AgGridReact) => {
      const { extraData: cliJob, ...data } = await quoteService.getCustomerQuotes({
        filters: {
          ...mapFilters(filters),
          isOrder: QuoteEntityType.ORDER === entityType ? 1 : 0,
          ...(job?.id ? { bomId: job.id } : {}),
          ...(projectId ? { projectId } : {}),
        },
        ...rest,
      });

      startCheckForPendingCoreCliJob(gridRef?.api, cliJob);

      return data;
    },
    [entityType, job, mapFilters, projectId, startCheckForPendingCoreCliJob]
  );

  const statusBar = useMemo<{
    statusPanels: StatusPanelDef[];
  }>(
    () => ({
      statusPanels: [
        {
          statusPanel: TotalsStatusBar,
          statusPanelParams:
            QuoteEntityType.ORDER === entityType ? { totalLabel: t('labelOrderValue') } : null,
          align: 'left',
        },
      ],
    }),
    [entityType, t]
  );

  const excelExportParams = useMemo(
    () => ({
      processCellCallback: (params: ProcessCellForExportParams) => {
        const colId = params.column.getColId();
        if (colId === CustomerQuoteColId.ARCHIVED) {
          return params.value === 0 ? t('labelActive') : t('labelArchived');
        }

        if (colId === CustomerQuoteColId.STATUS) {
          return QuoteStatusLabel[params.value as QuoteStatus];
        }

        return params.value;
      },
    }),
    [t]
  );

  const getHiddenFields = useCallback(() => {
    switch (quoteListType) {
      case QuoteListType.PROJECT:
        return {
          project: true,
        };
      case QuoteListType.JOB:
        return {
          project: true,
          jobName: true,
        };
      default:
        return {};
    }
  }, [quoteListType]);

  const selectionActionButtons = useMemo(() => {
    const buttons = [
      <DuplicateQuoteButton key="duplicateQuote" entityType={entityType} />,
      <ReassignQuoteButton
        key="reassignQuote"
        entityType={entityType}
        quoteType={QuoteType.CUSTOMER}
      />,
      <ArchiveQuoteButton
        key="archiveQuote"
        entityType={entityType}
        quoteType={QuoteType.CUSTOMER}
      />,
    ];

    if (QuoteEntityType.QUOTE === entityType) {
      const quoteButtons = [
        <UpdateQuotesButton key="updateQuotes" />,
        <ExportQuotesButton key="exportQuotes" />,
      ];

      buttons.push(...quoteButtons);
    }

    return buttons;
  }, [entityType]);

  return (
    <AgGridServerTableProvider
      key={user?.id}
      tableId={tableId}
      entityType={tableEntityType}
      columnDefs={columnDefs}
      statusBar={statusBar}
      initialState={initialState}
      getRows={getRows}
      hasLayoutActions
      hasManageFilters
      layoutType={layoutType}
      excelExportParams={excelExportParams}
    >
      <AgGridServerTable actions={actions} selectionActionButtons={selectionActionButtons} />

      <CustomerQuoteUpsert
        isOpen={QuoteListAction.ADD_QUOTE === activeActionDrawer}
        onClose={onCloseActionDrawer}
        onSubmitSuccess={(quote: ICustomerQuote) => {
          if (QuoteEntityType.ORDER === entityType) {
            navigate(orderPartsPath(quote.id));

            return;
          }

          navigate(quotePartsPath(quote.id));
        }}
        entityType={entityType}
        hiddenFields={getHiddenFields()}
        defaultValues={upsertCustomerQuoteDefaultValues}
      />

      {canCreateProspectQuote && (
        <ProspectQuoteUpsert
          isOpen={QuoteListAction.PROSPECT_QUOTE === activeActionDrawer}
          onClose={onCloseActionDrawer}
        />
      )}
    </AgGridServerTableProvider>
  );
};

export default CustomerQuoteList;
